import _get from 'lodash/get'
import { z } from 'zod'

import { HTTP_METHOD } from '@nxt-insurance/api-service'

import { formatClaimSublimit } from '@flows/configs/dental/utils/format'
import type { StepConfigParams } from '@flows/configs/types'
import type { FlowValues } from '@flows/core/types/stepTypes'

export const fetchClaimSublimits = async (params: StepConfigParams, flowValues: FlowValues) => {
  const contractStillActive = _get(
    flowValues,
    params.helperFunctions.getParamPath('dental_coverage_previous_still_active')
  )

  const payload = {
    previous_insurance: 'true',
    previous_insurance_start_date: _get(
      flowValues,
      params.helperFunctions.getParamPath('previous_insurance_start_date')
    ),
    previous_insurance_end_date: _get(
      flowValues,
      params.helperFunctions.getParamPath('previous_insurance_end_date')
    ),
  }

  if (contractStillActive) {
    delete payload.previous_insurance_end_date
  }

  const response = await params.clientHelpers.apiService.call({
    serviceName: 'insurance',
    method: HTTP_METHOD.POST,
    url: `/api/v2/markets/${params.clientHelpers.getMarket()}/dental/claim_sublimits`,
    payload,
  })
  return response?.data
}

export const claimSublimitFormValues = (
  params: StepConfigParams,
  claimSublimits: Awaited<ReturnType<typeof fetchClaimSublimits>>
) => ({
  [params.helperFunctions.getTransientPath('claim_sublimit_for_first_year')]: formatClaimSublimit(
    claimSublimits?.claim_sublimit_for_first_year,
    params
  ),
  [params.helperFunctions.getTransientPath('claim_sublimit_for_second_year')]: formatClaimSublimit(
    claimSublimits?.claim_sublimit_for_second_year,
    params
  ),
  [params.helperFunctions.getTransientPath('claim_sublimit_for_third_year')]: formatClaimSublimit(
    claimSublimits?.claim_sublimit_for_third_year,
    params
  ),
})

const insurerBrandsSchema = z.array(z.object({ key: z.string(), name: z.string() }))

export const previousInsurerName = (params: StepConfigParams, flowValues: FlowValues) => {
  const previousInsurerKey = _get(
    flowValues,
    params.helperFunctions.getParamPath('previous_insurer')
  )

  const insuranceBrands = insurerBrandsSchema.safeParse(
    _get(flowValues, params.helperFunctions.getTransientPath('insurer_brands'))
  )

  return insuranceBrands.data?.find((brand) => brand.key === previousInsurerKey)?.name
}

export const claimSublimitsPrepare = (
  params: StepConfigParams,
  { withInsurer = false }: { withInsurer?: boolean } = {}
) =>
  ({
    type: 'simpleHook',
    callback: async (flowValues: FlowValues) => ({
      formValues: {
        ...claimSublimitFormValues(params, await fetchClaimSublimits(params, flowValues)),
        ...(withInsurer && {
          [params.helperFunctions.getTransientPath('insurer')]: previousInsurerName(
            params,
            flowValues
          ),
        }),
      },
    }),
  }) as const
